import { useEffect, useState } from "react";
import { GuiButton } from "../../components/global-ui/gui-button";
import { showToast } from "../../components/toast";
import actions from "../../actions/actions";
import { dispatch } from "../../redux/redux-config";
import { Path } from "../../utilities/constants";

export default function AccountFacebookSection() {
  const FACEBOOK_OAUTH_STATE_KEY = "facebookOauthExpectedState";

  const [fbPostsEnabled, setFbPostsEnabled] = useState(false);
  const [facebookStatus, setFacebookStatus] = useState({
    loading: true,
    connected: false,
    displayName: "",
    pages: [],
    selectedPageId: null,
  });
  const [facebookBusy, setFacebookBusy] = useState(false);
  const [showPageSelection, setShowPageSelection] = useState(false);
  const [tempUserToken, setTempUserToken] = useState(null);
  const [tempPages, setTempPages] = useState([]);

  useEffect(() => {
    handleFacebookCallback();
    loadFacebookStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadFacebookStatus() {
    const result = await actions.facebook.getStatus();
    if (result?.success) {
      setFacebookStatus({
        loading: false,
        connected: !!result.connected,
        displayName: result.displayName || "",
        pages: result.pages || [],
        selectedPageId: result.selectedPageId || null,
      });
      setFbPostsEnabled(!!result.postsEnabled && !!result.connected);
      if (
        result.errorCode === "FACEBOOK_RECONNECT_REQUIRED" ||
        !result.connected
      ) {
        setFbPostsEnabled(false);
      }
    } else {
      setFacebookStatus((prev) => ({
        ...prev,
        loading: false,
        connected: false,
        pages: [],
      }));
      setFbPostsEnabled(false);
    }
  }

  function clearFacebookCallbackQuery() {
    if (!window.location.search) return;
    window.history.replaceState({}, document.title, Path.AccountGeneral);
  }

  async function handleFacebookCallback() {
    const queryParams = new URLSearchParams(window.location.search);
    const error = queryParams.get("error");
    const errorDescription = queryParams.get("error_description");
    const code = queryParams.get("code");

    const expectedState = sessionStorage.getItem(FACEBOOK_OAUTH_STATE_KEY);

    if (!expectedState && !error && !code) {
      return;
    }

    if (error) {
      showToast(
        "error",
        decodeURIComponent(
          errorDescription || "Facebook sign-in was cancelled.",
        ),
      );
      sessionStorage.removeItem(FACEBOOK_OAUTH_STATE_KEY);
      clearFacebookCallbackQuery();
      return;
    }

    if (!code) {
      return;
    }

    setFacebookBusy(true);

    try {
      const tokenResult = await actions.facebook.callback(code);

      if (!tokenResult?.success || !tokenResult.userAccessToken) {
        showToast("error", "Failed to authenticate with Facebook.");
        sessionStorage.removeItem(FACEBOOK_OAUTH_STATE_KEY);
        clearFacebookCallbackQuery();
        setFacebookBusy(false);
        return;
      }

      const saveResult = await actions.facebook.authenticate(
        null,
        null,
        null,
        null,
        tokenResult.userAccessToken,
      );

      if (!saveResult?.success) {
        showToast("error", "Failed to save Facebook connection.");
        sessionStorage.removeItem(FACEBOOK_OAUTH_STATE_KEY);
        clearFacebookCallbackQuery();
        setFacebookBusy(false);
        return;
      }

      const pagesResult = await actions.facebook.getPages(
        tokenResult.userAccessToken,
      );

      if (pagesResult?.errorCode === "FACEBOOK_RECONNECT_REQUIRED") {
        showToast(
          "error",
          pagesResult.errorMessage || "Facebook reconnect required.",
        );
        setFacebookBusy(false);
        await loadFacebookStatus();
        return;
      }

      if (pagesResult?.success && pagesResult.pages?.length > 0) {
        setTempUserToken(tokenResult.userAccessToken);
        setTempPages(pagesResult.pages);
        setShowPageSelection(true);
        setFacebookBusy(false);
      } else if (pagesResult?.success) {
        await loadFacebookStatus();
        setFacebookBusy(false);
      } else {
        showToast(
          "error",
          pagesResult?.errorMessage || "Failed to load Facebook pages.",
        );
        setFacebookBusy(false);
      }
    } catch (err) {
      showToast("error", "An error occurred during Facebook authentication.");
      setFacebookBusy(false);
    } finally {
      sessionStorage.removeItem(FACEBOOK_OAUTH_STATE_KEY);
      clearFacebookCallbackQuery();
    }
  }

  async function connectFacebook() {
    setFacebookBusy(true);
    const result = await actions.facebook.getAuthUrl();

    if (result?.success && result.url) {
      if (result.state) {
        sessionStorage.setItem(FACEBOOK_OAUTH_STATE_KEY, result.state);
      }
      window.location.href = result.url;
      return;
    }

    showToast("error", "Failed to initiate Facebook connection.");
    setFacebookBusy(false);
  }

  async function disconnectFacebook() {
    setFacebookBusy(true);
    const result = await actions.facebook.disconnect();
    if (result?.success) {
      setFbPostsEnabled(false);
      dispatch("LOAD_USER_INFO", { facebookPostAllow: false });
      setShowPageSelection(false);
      setTempUserToken(null);
      setTempPages([]);
      await loadFacebookStatus();
    }
    setFacebookBusy(false);
  }

  function openFacebookPageSelection() {
    if (tempPages.length > 0) {
      setShowPageSelection(true);
      return;
    }

    if (facebookStatus.pages?.length > 0) {
      setTempPages(facebookStatus.pages);
      setShowPageSelection(true);
      return;
    }

    showToast(
      "error",
      "No Facebook pages are available to select right now. Reconnect Facebook to refresh your managed pages.",
    );
  }

  async function selectFacebookPage(page) {
    setFacebookBusy(true);

    const result = tempUserToken
      ? await actions.facebook.authenticate(
          null,
          page.pageId,
          page.pageName,
          page.pageAccessToken,
          tempUserToken,
        )
      : await actions.facebook.changePage(
          page.pageId,
          page.pageName,
          page.pageAccessToken,
        );

    if (result?.success) {
      setShowPageSelection(false);
      setTempUserToken(null);
      setTempPages([]);
      await loadFacebookStatus();
    } else {
      showToast(
        "error",
        tempUserToken
          ? "Failed to connect Facebook page."
          : "Failed to change Facebook page.",
      );
    }

    setFacebookBusy(false);
  }

  async function cancelPageSelection() {
    const shouldRefreshStatus = !!tempUserToken;

    setShowPageSelection(false);
    setTempUserToken(null);
    setTempPages([]);

    if (shouldRefreshStatus) {
      setFacebookBusy(true);
      try {
        await loadFacebookStatus();
      } finally {
        setFacebookBusy(false);
      }
    }
  }

  async function toggleFacebookPosts() {
    if (!facebookStatus.connected) {
      showToast("error", "Please connect your Facebook account first.");
      return;
    }

    if (!facebookStatus.selectedPageId) {
      showToast("error", "Please choose a Facebook page first.");
      return;
    }

    const newValue = !fbPostsEnabled;
    setFbPostsEnabled(newValue);

    setFacebookBusy(true);
    const result = await actions.facebook.togglePosting();

    if (!result?.success) {
      setFbPostsEnabled(!newValue);
      showToast("error", "Failed to update Facebook posting setting.");
    } else if (typeof result.postsEnabled === "boolean") {
      setFbPostsEnabled(result.postsEnabled);
      dispatch("LOAD_USER_INFO", { facebookPostAllow: result.postsEnabled });
    } else {
      dispatch("LOAD_USER_INFO", { facebookPostAllow: newValue });
    }

    setFacebookBusy(false);
  }

  const isFacebookConnected = facebookStatus.connected;
  const isFacebookLoading = facebookStatus.loading || facebookBusy;
  const facebookActionText = isFacebookConnected
    ? "Disconnect Facebook"
    : "Sign in to Facebook";
  const hasPageOptions =
    tempPages.length > 0 || facebookStatus.pages?.length > 0;
  const selectedPage =
    facebookStatus.pages.find(
      (page) => page.pageId === facebookStatus.selectedPageId,
    ) || null;
  const pageSelectionActionText = facebookStatus.selectedPageId
    ? "Change Facebook Page"
    : "Choose Facebook Page";
  const modalDismissText = isFacebookConnected ? "Cancel" : "Skip for Now";

  return (
    <>
      <div className="account-social__item account-social__item--facebook">
        <div className="account-social__item-header">
          <svg
            className="account-social__icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.791-4.697 4.533-4.697 1.313 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.887v2.264h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z"
              fill="#1877F2"
            />
          </svg>
          <span className="account-social__name">Facebook</span>
          <span
            className={`account-social__badge ${isFacebookConnected ? "account-social__badge--connected" : "account-social__badge--disconnected"}`}
          >
            {isFacebookConnected
              ? `Connected${facebookStatus.displayName ? `: ${facebookStatus.displayName}` : ""}`
              : "Disconnected"}
          </span>
        </div>
        <GuiButton
          className={`btn-outline account-social__connect-btn ${isFacebookConnected ? "account-social__connect-btn--disconnect" : "account-social__connect-btn--connect"}`}
          disabled={facebookBusy || facebookStatus.loading}
          onClick={isFacebookConnected ? disconnectFacebook : connectFacebook}
        >
          {isFacebookLoading ? (
            <span className="account-social__btn-loading">
              <span
                className="account-social__spinner account-social__spinner--sm"
                aria-hidden="true"
              />
              {isFacebookConnected ? "Processing..." : "Connecting..."}
            </span>
          ) : (
            facebookActionText
          )}
        </GuiButton>

        {isFacebookLoading && (
          <div
            className="account-social__loading"
            role="status"
            aria-live="polite"
          >
            <span className="account-social__spinner" aria-hidden="true" />
            <span className="account-social__loading-text">
              {facebookStatus.loading
                ? "Loading Facebook connection status..."
                : isFacebookConnected
                  ? "Updating Facebook settings..."
                  : "Connecting your Facebook account..."}
            </span>
          </div>
        )}

        {isFacebookConnected && hasPageOptions && (
          <div className="account-social__page-info">
            <div className="account-social__page-info-main">
              <span className="account-social__page-label">Selected Page:</span>
              <span className="account-social__page-name-wrap">
                <span
                  className="account-social__page-selected-icon"
                  aria-hidden="true"
                >
                  {selectedPage?.pageIconUrl ? (
                    <img
                      className="account-social__page-icon-image"
                      src={selectedPage.pageIconUrl}
                      alt=""
                    />
                  ) : (
                    <i className="fab fa-facebook-square account-social__page-icon-fallback" />
                  )}
                </span>
                <span className="account-social__page-name">
                  {selectedPage?.pageName || "Not selected"}
                </span>
              </span>
              <div className="account-social__page-actions">
                <GuiButton
                  className="btn-outline account-social__page-action-btn"
                  disabled={facebookBusy}
                  onClick={openFacebookPageSelection}
                >
                  {pageSelectionActionText}
                </GuiButton>
              </div>
            </div>
          </div>
        )}

        <div className="account-social__toggle-row">
          <span className="account-social__toggle-label">
            Allow posts to Facebook pages you manage
          </span>
          <button
            className={`account-social__toggle${fbPostsEnabled ? " account-social__toggle--on" : ""}`}
            onClick={toggleFacebookPosts}
            aria-pressed={fbPostsEnabled}
            aria-label={
              fbPostsEnabled
                ? "Disable posting to Facebook pages"
                : "Enable posting to Facebook pages"
            }
            disabled={
              !isFacebookConnected ||
              !facebookStatus.selectedPageId ||
              facebookBusy
            }
          />
        </div>
      </div>

      {showPageSelection && (
        <div
          className="account-social__modal-overlay"
          onClick={cancelPageSelection}
        >
          <div
            className="account-social__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="account-social__modal-header">
              <h3 className="account-social__modal-title">
                Select a Facebook Page
              </h3>
              <button
                className="account-social__modal-close"
                onClick={cancelPageSelection}
                disabled={facebookBusy}
                aria-label="Close page selection dialog"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="account-social__modal-body">
              {facebookBusy && (
                <div
                  className="account-social__modal-loading"
                  role="status"
                  aria-live="polite"
                >
                  <span
                    className="account-social__spinner"
                    aria-hidden="true"
                  />
                  <span className="account-social__loading-text">
                    Applying selected Facebook page...
                  </span>
                </div>
              )}
              <p className="account-social__modal-description">
                Choose a Facebook page to post messages when sending alerts.
              </p>
              <div className="account-social__page-list">
                {tempPages.map((page) => (
                  <button
                    key={page.pageId}
                    className="account-social__page-item"
                    onClick={() => selectFacebookPage(page)}
                    disabled={facebookBusy}
                  >
                    <div
                      className="account-social__page-item-icon"
                      aria-hidden="true"
                    >
                      {page.pageIconUrl ? (
                        <img
                          className="account-social__page-icon-image"
                          src={page.pageIconUrl}
                          alt=""
                        />
                      ) : (
                        <i className="fab fa-facebook-square account-social__page-icon-fallback" />
                      )}
                    </div>
                    <div className="account-social__page-item-info">
                      <span className="account-social__page-item-name">
                        {page.pageName}
                      </span>
                      <span className="account-social__page-item-id">
                        ID: {page.pageId}
                      </span>
                    </div>
                    <i className="fas fa-chevron-right account-social__page-item-arrow" />
                  </button>
                ))}
              </div>
            </div>
            <div className="account-social__modal-footer">
              <GuiButton
                className="btn-outline account-social__modal-action-btn"
                onClick={cancelPageSelection}
                disabled={facebookBusy}
              >
                {modalDismissText}
              </GuiButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
